import { useState, createContext, ReactNode } from 'react';
import { CoinInfoStore } from '@/types/common';
import { GetCoinInfoParams } from '@/types/wallet';
import { getCoinInfo, getCoinPrice } from '@/services/wallet';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { defaultCoinInfo } from '@/config';
import { CoinInfo } from '@/types/common';
import { ethAddress } from '@/config/address';
import { isLBTCvPool, isLombardPool } from '@/utils/common';

export const CoinInfoContext = createContext<{
  isFetching: boolean;
  coinInfoStore: CoinInfoStore;
  combindCoinInfo: (list: CoinInfo[]) => void;
  fetchCoinInfoCore: (
    getCoinInfoParamsArr: GetCoinInfoParams[]
  ) => Promise<CoinInfoStore>;
  fetchCoinInfo: (
    getCoinInfoParamsArr: GetCoinInfoParams[]
  ) => Promise<CoinInfoStore>;
}>({
  isFetching: true,
  coinInfoStore: {},
  // eslint-disable-next-line
  combindCoinInfo: () => undefined,
  fetchCoinInfoCore: () => Promise.resolve({}),
  fetchCoinInfo: () => Promise.resolve({}),
});

CoinInfoContext.displayName = 'CoinInfoContext';
interface Props {
  children: ReactNode;
}

export const CoinInfoProvider = (props: Props) => {
  const { children } = props;
  const [coinInfoStore, setCoinInfoStore] = useState<CoinInfoStore>({});
  const [isFetching, setIsFetching] = useState(true);
  const { poolInfo } = usePoolInfoContext();

  // 请求新数据
  const fetchCoinInfoCore = async (
    getCoinInfoParamsArr: GetCoinInfoParams[]
  ) => {
    const coinInfoStore: CoinInfoStore = {};

    await Promise.allSettled(
      getCoinInfoParamsArr.map(async (getCoinInfoParams) => {
        try {
          const [
            { success: coinInfoSuccess, result: coinInfoResult },
            { result: coinPriceResult },
          ] = await Promise.all([
            getCoinInfo(getCoinInfoParams),
            getCoinPrice(getCoinInfoParams),
          ]);

          if (coinInfoSuccess) {
            // coininfo可能不返回，则使用兜底数据
            getCoinInfoParams.contractAddresses.forEach((contract) => {
              const { chainCode } = getCoinInfoParams;
              const coinInfo = coinInfoResult[contract] || coinInfoResult[''];
              let coinPrice = '';
              if (contract === ethAddress) {
                // 如果参数是ethAddress，返回的key可能是''
                coinPrice = coinPriceResult[ethAddress] || coinPriceResult[''];
              } else {
                coinPrice = coinPriceResult[contract];
              }

              coinInfoStore[`${chainCode}${contract}`] = {
                // 兜底兼容
                ...defaultCoinInfo,
                ...coinInfo,
                contractAddress: contract,
                price: coinPrice ?? '', // 可能不返回
              };
            });
          }
        } catch (e) {
          return Promise.resolve();
        }
      })
    );

    return coinInfoStore;
  };

  // 拼装结构数据
  const combindCoinInfo = (list: CoinInfo[]) => {
    const coinInfoStore: CoinInfoStore = {};
    list.forEach((item) => {
      const key = `${isLombardPool(poolInfo?.id) ? item?.coinDetailJson?.chainCode : poolInfo.chainCode}${item.contractAddress}`;

      coinInfoStore[key] = {
        ...item,
        ...item.coinDetailJson,
      };
    });

    setCoinInfoStore((cache) => ({
      ...cache,
      ...coinInfoStore,
    }));

    return coinInfoStore;
  };

  const fetchCoinInfo = async (getCoinInfoParamsArr: GetCoinInfoParams[]) => {
    setIsFetching(true);

    const coinInfoStore: CoinInfoStore =
      await fetchCoinInfoCore(getCoinInfoParamsArr);

    setCoinInfoStore((cache) => ({
      ...cache,
      ...coinInfoStore,
    }));

    setIsFetching(false);

    return {
      ...coinInfoStore,
    } as CoinInfoStore;
  };

  return (
    <CoinInfoContext.Provider
      value={{
        coinInfoStore,
        isFetching,
        combindCoinInfo,
        fetchCoinInfo,
        fetchCoinInfoCore,
      }}
    >
      {children}
    </CoinInfoContext.Provider>
  );
};
